import React, { useState, useEffect, useRef } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import FontAwesome from 'react-fontawesome';
import PlusIcon from '~/Components/Global/Assets/PlusIcon';
import { createFolder, deleteFolder } from '~/Redux/Modules/Folders';
import Folder from './Folder';
import ListOption from './ListOption';
import PinnedListOption from './PinnedListOption';
import SortDropDown from './SortDropDown';

const DropDownMenu = props => {
  const {
    userLists,
    onChangeList,
    dispatchCreateFolder,
    current,
    handleListDropDownClick,
    userFolders,
    dispatchDeleteFolder,
    toggleCreateList,
    sortOptions,
    activeFolderId,
    handleChangeActiveFolder,
    handleActiveFolderIsActive,
    showSubMenu,
    handleShowSubMenu,
    handleModal,
  } = props;
  const [createNewFolder, setCreateNewFolder] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [lists, setLists] = useState([...userLists]);
  const [pinnedLists, setPinnedLists] = useState([...userLists]);
  const [folders, setFolders] = useState([...userFolders]);
  const [openListsSortDropdown, setOpenListsSortDropdown] = useState(false);
  const [openFoldersSortDropdown, setOpenFoldersSortDropdown] = useState(false);
  const [activeListSortOption, setActiveListSortOption] = useState(
    localStorage.getItem('activeListSortOption') || 'Date last modified'
  );
  const [activeFolderSortOption, setActiveFolderSortOption] = useState(
    localStorage.getItem('activeFolderSortOption') || 'Date last modified'
  );
  const dropdownAreaRef = useRef(null);

  const [selectedListOption, setSelectedListOption] = useState(
    localStorage.getItem('activeListSortOption')
  );
  const [selectedFolderOption, setSelectedFolderOption] = useState(
    localStorage.getItem('activeFolderSortOption')
  );
  const foldersClassName = 'sort-folders-drop-down';
  const listsClassName = 'sort-lists-drop-down';

  const toggleCreate = e => {
    e.stopPropagation();
    e.preventDefault();
    if (!createNewFolder) {
      setCreateNewFolder(true);
      setNewFolder('');
    } else if (createNewFolder) {
      setCreateNewFolder(false);
    }
  };

  const handleClickCreateNewList = () => {
    toggleCreateList();
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSubMenu]);
  const handleClickOutside = event => {
    const hasAncestorWithClass = (element, className) => {
      while (element) {
        if (element.classList && element.classList.contains(className)) {
          return true;
        }
        element = element.parentElement;
      }
      return false;
    };

    const triggeringElement = event.target;
    const classNameToCheck = 'do-not-close-dropdown';
    if (
      !dropdownAreaRef.current.contains(triggeringElement) &&
      !hasAncestorWithClass(triggeringElement, classNameToCheck)
    ) {
      if (showSubMenu) {
        // Close secondary submenu
        handleFolderClick(event, false, null);
        handleShowSubMenu(false, null);
      } else {
        // Close primary menu
        handleListDropDownClick();
      }
    }
  };

  useEffect(() => {
    setLists([...userLists]);
    setFolders([...userFolders]);
    setPinnedLists([...userLists]);
  }, [userLists, userFolders, userFolders.length, current]);

  const pinnedListsToRender = pinnedLists
    .filter(list => list.pinned)
    .sort((a, b) => b.updated_at - a.updated_at)
    .map(list => {
      return (
        <PinnedListOption
          handleModal={handleModal}
          handleListDropDownClick={handleListDropDownClick}
          key={list.id}
          list={list}
          onChangeList={onChangeList}
          current={current}
        />
      );
    });

  const handleCreateNewFolder = folder => {
    dispatchCreateFolder({ title: folder });
    setNewFolder('');
    setCreateNewFolder(false);
  };

  const getLastUpdatedFromLists = folder => {
    const folderLists = lists.filter(list => list.list_group_id === folder.id);
    if (folderLists.length > 0) {
      const lastUpdatedList = folderLists.reduce((prev, current) => {
        return prev.updated_at > current.updated_at ? prev : current;
      });
      return lastUpdatedList.updated_at;
    }
    return folder.created_at;
  };

  const sortListByOption = option => {
    switch (option) {
      case 'Title A - Z':
        return [...lists].sort((a, b) => a.title.localeCompare(b.title));
      case 'Title Z - A':
        return [...lists].sort((a, b) => b.title.localeCompare(a.title));
      case 'Newest to oldest':
        return [...lists].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'Oldest to newest':
        return [...lists].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'Date last modified':
        return [...lists].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      default:
        return lists;
    }
  };

  const sortFolderByOption = option => {
    switch (option) {
      case 'Title A - Z':
        return [...folders].sort((a, b) => a.title.localeCompare(b.title));
      case 'Title Z - A':
        return [...folders].sort((a, b) => b.title.localeCompare(a.title));
      case 'Newest to oldest':
        return [...folders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'Oldest to newest':
        return [...folders].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'Date last modified':
        return [...folders].sort((a, b) => {
          const aLastUpdated = getLastUpdatedFromLists(a);
          const bLastUpdated = getLastUpdatedFromLists(b);
          return new Date(bLastUpdated) - new Date(aLastUpdated);
        });
      default:
        return folders;
    }
  };

  const handleChangeActiveFolderAndIsActive = (isActive, folderId) => {
    handleChangeActiveFolder(isActive ? folderId : null);
    handleActiveFolderIsActive(isActive);
  };

  const handleFolderClick = (e, isActive, folderId) => {
    e.stopPropagation();
    e.preventDefault();
    handleShowSubMenu(isActive, folderId);
    handleChangeActiveFolderAndIsActive(isActive, folderId);
  };

  const foldersList = sortFolderByOption(activeFolderSortOption).map(folder => {
    const isActive = folder.id === activeFolderId;
    return (
      <Folder
        handleModal={handleModal}
        current={current}
        key={folder.id}
        deleteFolder={dispatchDeleteFolder}
        folder={folder}
        lists={userLists}
        onChangeList={onChangeList}
        isActive={isActive}
        handleFolderClick={handleFolderClick}
        showSubMenu={showSubMenu}
      />
    );
  });

  const handleSortLists = opt => {
    setActiveListSortOption(opt);
    localStorage.setItem('activeListSortOption', opt);
    setLists(sortListByOption(opt));
  };

  const handleSortFolders = opt => {
    setActiveFolderSortOption(opt);
    localStorage.setItem('activeFolderSortOption', opt);
    setFolders(sortFolderByOption(opt));
  };

  const listsToRender = sortListByOption(activeListSortOption)
    .filter(list => list.list_group_id === null && !list.pinned)
    .map(list => {
      return (
        <ListOption
          lowerSection
          handleListDropDownClick={handleListDropDownClick}
          key={list.id}
          list={list}
          onChangeList={onChangeList}
          current={current}
          handleModal={handleModal}
        />
      );
    });

  const handleKeyDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
      if (newFolder.trim() !== '') {
        handleCreateNewFolder(newFolder);
      }
    }
  };

  return (
    <div className="lists-dropdown-menu" ref={dropdownAreaRef} id="lists-dropdown-menu">
      <div className="pinned-section">{pinnedListsToRender}</div>
      {pinnedListsToRender.length >= 1 && <hr className="dropdown-divider" />}
      {foldersList.length > 1 && (
        <div className="sort-folders">
          <span
            onClick={() => setOpenFoldersSortDropdown(current => !current)}
            className="sort-folders-dropdown"
            tabIndex={0}
            role="button"
          >
            Sort: {activeFolderSortOption}
            <FontAwesome
              className={`sort-arrow ${openFoldersSortDropdown ? 'sort-arrow-open' : ''}`}
              name="caret-up"
              size="lg"
            />
          </span>
          {openFoldersSortDropdown && folders.length >= 1 && (
            <SortDropDown
              className={foldersClassName}
              setSelectedOption={setSelectedFolderOption}
              selectedOption={selectedFolderOption}
              sortDropdownOpen={openFoldersSortDropdown}
              handleSort={handleSortFolders}
              setOpenSortDropdown={setOpenFoldersSortDropdown}
              sortOptions={sortOptions}
            />
          )}
        </div>
      )}
      <div className="folders-list-section">{foldersList}</div>
      {createNewFolder && (
        <input
          autoFocus
          onKeyDown={handleKeyDown}
          value={newFolder}
          name="newFolder"
          placeholder="My New Folder"
          className="new-folder-input"
          onChange={e => setNewFolder(e.target.value)}
          onBlur={e => toggleCreate(e)}
        />
      )}
      <div
        onClick={e => toggleCreate(e)}
        className={` ${listsToRender.length >= 1 ? '' : 'lists-section-hidden'} ${
          createNewFolder ? 'hide-new-folder' : 'new-folder'
        }`}
      >
        <PlusIcon className="new-folder-plus-icon" /> <span>New Folder</span>
      </div>
      <hr className="dropdown-divider" />
      {listsToRender.length >= 1 && (
        <>
          {listsToRender.length > 1 ? (
            <div className="sort-lists">
              <span
                onClick={() => setOpenListsSortDropdown(current => !current)}
                className="sort-lists-dropdown"
                tabIndex={0}
                role="button"
              >
                Sort: {activeListSortOption}
                <FontAwesome
                  className={`sort-arrow ${openListsSortDropdown ? 'sort-arrow-open' : ''}`}
                  name="caret-up"
                  size="lg"
                />
              </span>
              {openListsSortDropdown && lists.length >= 1 && (
                <SortDropDown
                  className={listsClassName}
                  setSelectedOption={setSelectedListOption}
                  selectedOption={selectedListOption}
                  sortDropdownOpen={openListsSortDropdown}
                  handleSort={handleSortLists}
                  setOpenSortDropdown={setOpenListsSortDropdown}
                  sortOptions={sortOptions}
                />
              )}
            </div>
          ) : null}
          <div className="bottom-section">{listsToRender}</div>
        </>
      )}
      <div
        tabIndex="0"
        role="button"
        onClick={() => handleClickCreateNewList()}
        className="create-new-list new-list-section"
      >
        <PlusIcon className="new-list-plus-icon new-list-section" />{' '}
        <span className="new-list-section">New List</span>
      </div>
    </div>
  );
};
DropDownMenu.propTypes = {
  userLists: propTypes.array,
  userFolders: propTypes.array,
  onChangeList: propTypes.func,
  createFolder: propTypes.func,
  dispatchCreateFolder: propTypes.func,
  dispatchDeleteFolder: propTypes.func,
  handleListDropDownClick: propTypes.func,
  toggleCreateList: propTypes.func,
  current: propTypes.oneOfType([propTypes.object, propTypes.bool]),
  sortOptions: propTypes.array,
  activeFolderId: propTypes.oneOfType([propTypes.number, propTypes.string]),
  handleChangeActiveFolder: propTypes.func,
  handleActiveFolderIsActive: propTypes.func,
  showSubMenu: propTypes.bool,
  handleShowSubMenu: propTypes.func,
  handleModal: propTypes.func,
};
const mapStateToProps = () => ({});
const mapDispatchToProps = dispatch => {
  return {
    dispatchCreateFolder: data => dispatch(createFolder(data)),
    dispatchDeleteFolder: data => dispatch(deleteFolder(data)),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(DropDownMenu);
